import React from 'react';

export interface DataTableColumn<Row> {
  key: string;
  label: React.ReactNode;
  /** Render the cell. Defaults to `String(row[key])` if the key indexes the row. */
  render?: (row: Row) => React.ReactNode;
  /** Optional CSS width — `auto`, `120px`, `1fr`, etc. Used for thead width hints. */
  width?: string;
  /** Optional className applied to both th and td. */
  className?: string;
  /** Mark this column sticky (left-pinned during horizontal scroll). */
  sticky?: boolean;
  /** Plain-text label used by mobile-card mode via the `data-th` attribute. Defaults to `label` if it is a string. */
  mobileLabel?: string;
}

export type DataTableDensity = 'comfortable' | 'dense';

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  /** Stable key per row. */
  keyOf: (row: Row, index: number) => string | number;
  /** Optional actions cell rendered as the last column. */
  actionsColumn?: {
    label?: React.ReactNode;
    render: (row: Row) => React.ReactNode;
    width?: string;
    mobileLabel?: string;
  };
  /** Loading-spinner overlay (rows still rendered if present). */
  loading?: boolean;
  /** Empty-state copy when rows is empty and not loading. */
  emptyMessage?: React.ReactNode;
  /** Initial-load placeholder when rows is empty AND loading. */
  loadingMessage?: React.ReactNode;
  /** Optional wrapper className for one-off styling. */
  className?: string;
  /** Row density. Defaults to `comfortable` (56 px min-height); `dense` is 40 px. */
  density?: DataTableDensity;
  /** When true, alternate row backgrounds. */
  striped?: boolean;
  /** Slot rendered above the `<table>` element (typically `<AddButton>` or filter chips). */
  aboveTable?: React.ReactNode;
  /** Slot rendered below the `<table>` element in a footer wrapper. */
  pagination?: React.ReactNode;
  /** Collapse each row into a stacked card at `<= $tablet`. */
  mobileCard?: boolean;
}

function mobileLabelFor<Row>(col: DataTableColumn<Row>): string | undefined {
  if (col.mobileLabel) return col.mobileLabel;
  if (typeof col.label === 'string') return col.label;
  if (typeof col.label === 'number') return String(col.label);
  return undefined;
}

export default function DataTable<Row>({
  columns,
  rows,
  keyOf,
  actionsColumn,
  loading = false,
  emptyMessage = 'No rows to display.',
  loadingMessage = 'Loading…',
  className,
  density = 'comfortable',
  striped = false,
  aboveTable,
  pagination,
  mobileCard = false,
}: DataTableProps<Row>) {
  const showInitialLoading = loading && rows.length === 0;
  const showEmpty = !loading && rows.length === 0;

  const renderCell = (col: DataTableColumn<Row>, row: Row): React.ReactNode => {
    if (col.render) return col.render(row);
    const value = (row as unknown as Record<string, unknown>)[col.key];
    if (value == null || value === '') return '—';
    return String(value);
  };

  const wrapperClass = [
    'admin-data-table',
    `admin-data-table--${density}`,
    striped ? 'admin-data-table--striped' : null,
    mobileCard ? 'admin-data-table--mobile-card' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const tableClass = [
    'admin-data-table__table',
    mobileCard ? 'admin-data-table__table--mobile-card' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClass}>
      {aboveTable && <div className="admin-data-table__above">{aboveTable}</div>}

      {loading && rows.length > 0 && (
        <div className="admin-data-table__loading-overlay">
          <div className="admin-data-table__spinner" />
        </div>
      )}

      <div className="admin-data-table__scroll">
        <table className={tableClass}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={
                    [
                      col.className,
                      col.sticky ? 'admin-data-table__cell--sticky' : null,
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </th>
              ))}
              {actionsColumn && (
                <th
                  className="admin-data-table__actions-col"
                  style={actionsColumn.width ? { width: actionsColumn.width } : undefined}
                >
                  {actionsColumn.label ?? 'Actions'}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {showInitialLoading ? (
              <tr>
                <td
                  className="admin-data-table__placeholder"
                  colSpan={columns.length + (actionsColumn ? 1 : 0)}
                >
                  {loadingMessage}
                </td>
              </tr>
            ) : showEmpty ? (
              <tr>
                <td
                  className="admin-data-table__placeholder"
                  colSpan={columns.length + (actionsColumn ? 1 : 0)}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={keyOf(row, index)}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      data-th={mobileCard ? mobileLabelFor(col) : undefined}
                      className={
                        [
                          col.className,
                          col.sticky ? 'admin-data-table__cell--sticky' : null,
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                    >
                      {renderCell(col, row)}
                    </td>
                  ))}
                  {actionsColumn && (
                    <td
                      data-th={mobileCard ? (actionsColumn.mobileLabel ?? 'Actions') : undefined}
                      className="admin-data-table__actions-cell"
                    >
                      {actionsColumn.render(row)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && <div className="admin-data-table__pagination">{pagination}</div>}
    </div>
  );
}
